/**
 * CutestPluckLandingPage — Cutest Pluck contest hub.
 *
 * Public route at `/cutest-pluck`. Renders:
 *   1. Hero: contest title, prize copy, entry CTA to `/contest/enter`
 *      (Phase A route).
 *   2. Voting-close countdown (only when `contest_config.voting_close_at`
 *      is populated — Phase A left it null; Phase C admin surface
 *      fills it in).
 *   3. Entries grid: photo tiles with dog_name, caption, vote count,
 *      per-tile Vote / Voted ✓ button.
 *   4. Sort control: Top (votes desc) / Recent (created_at desc).
 *   5. OG tags via react-helmet-async — locked-copy title +
 *      description + hero image URL.
 *
 * Voting flow:
 *   - Unauthed click → redirect to `/login?next=/cutest-pluck`.
 *   - Authed click → optimistic UI (immediate Voted ✓ + count bump),
 *     POST /api/contest/entries/{id}/vote, roll back on failure.
 *   - Self-vote tile shows "You're in the running!" instead of a
 *     Vote button, per Phase B spec.
 *   - Rate-limit 429 → toast + rollback.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { trackEvent } from "@/lib/analytics";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PublicPageShell from "@/components/PublicPageShell";
import PoliteStatus from "@/components/a11y/PoliteStatus";
import WinnerSlotCard from "@/components/WinnerSlotCard";
import { Trophy, Check, Heart, Loader2, ImageOff } from "lucide-react";
import { EntryShareButton } from "../../components/EntryShareButton";
import { useActiveContest } from "@/context/ActiveContestContext";

const OG_TITLE = "Cutest Pluck on the Block — PooPluckers";
// Fallback default matching backend's `get_active_round_or_seed`
// legacy-synth fallback at `contest_rounds.py:124` — used during the
// brief loading window before `/public/contest/active` resolves.
// Non-JS OG crawlers (Facebook, most Twitter) hit this constant via
// the SSR'd HTML they receive; JS-enabled crawlers see the dynamic
// value from `useActiveContest` after mount.
const DEFAULT_PRIZE_AMOUNT_CENTS = 2100;

/**
 * Format a cents amount as a display dollar string. Trailing `.00`
 * is stripped for whole-dollar values so `$21`, `$30` render cleanly;
 * fractional cents (e.g. $21.50) get 2-decimal precision. Consumed
 * for the landing hero prize sentence + OG description; kept as a
 * local helper (single-file use, no reason to hoist).
 */
function formatPrizeDollars(cents) {
  const c = Number.isFinite(cents) ? cents : DEFAULT_PRIZE_AMOUNT_CENTS;
  const dollars = c / 100;
  return dollars % 1 === 0
    ? `$${dollars.toFixed(0)}`
    : `$${dollars.toFixed(2)}`;
}

// Absolute URL derived from `window.location.origin` at render time
// so a share on staging/prod each get the right host. The hero image
// path is stable — see `frontend/public/og/cutest-pluck-hero.jpg`.
function absoluteUrl(path) {
  if (typeof window === "undefined") return path;
  return `${window.location.origin}${path}`;
}

function formatVoteCount(n) {
  return n === 1 ? "1 vote" : `${n} votes`;
}

function CountdownBadge({ closeAt }) {
  const [remainingMs, setRemainingMs] = useState(() =>
    Math.max(0, new Date(closeAt).getTime() - Date.now()),
  );
  useEffect(() => {
    const id = setInterval(
      () => setRemainingMs(Math.max(0, new Date(closeAt).getTime() - Date.now())),
      1000,
    );
    return () => clearInterval(id);
  }, [closeAt]);
  const days = Math.floor(remainingMs / (24 * 3600 * 1000));
  const hours = Math.floor((remainingMs % (24 * 3600 * 1000)) / (3600 * 1000));
  const mins = Math.floor((remainingMs % (3600 * 1000)) / (60 * 1000));
  return (
    <div
      className="inline-flex items-center gap-2 rounded-full bg-pp-surface-2 px-3 py-1 text-xs font-semibold text-pp-text"
      data-testid="cutest-pluck-countdown"
    >
      Voting closes in {days}d {hours}h {mins}m
    </div>
  );
}

function EntryTile({ entry, isMyEntry, hasVoted, onVote, pending }) {
  return (
    <div
      className="flex flex-col overflow-hidden rounded-3xl border border-border bg-pp-surface shadow-sm"
      data-testid={`cutest-pluck-tile-${entry.id}`}
    >
      <Link
        to={`/cutest-pluck/entries/${entry.id}`}
        className="block aspect-[4/3] w-full bg-pp-surface-2"
        data-testid={`cutest-pluck-tile-link-${entry.id}`}
      >
        {entry.photo_url ? (
          <img
            src={entry.photo_url}
            alt={`Photo of ${entry.dog_name}`}
            className="h-full w-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-muted-foreground">
            <ImageOff className="h-8 w-8" aria-hidden="true" />
          </div>
        )}
      </Link>
      <div className="flex flex-1 flex-col gap-3 p-4">
        <div>
          <h3
            className="font-heading text-lg font-bold text-pp-text"
            data-testid={`cutest-pluck-tile-name-${entry.id}`}
          >
            {entry.dog_name}
          </h3>
          {entry.caption && (
            <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">
              {entry.caption}
            </p>
          )}
        </div>
        <div className="mt-auto flex items-center justify-between">
          <div
            className="inline-flex items-center gap-1 text-sm font-semibold text-brand-orange"
            data-testid={`cutest-pluck-tile-count-${entry.id}`}
          >
            <Heart className="h-4 w-4" aria-hidden="true" />
            {formatVoteCount(entry.vote_count)}
          </div>
          <div className="flex items-center gap-1">
            <EntryShareButton entry={entry} />
            {isMyEntry ? (
              <span
                className="inline-flex items-center gap-1 rounded-full bg-pp-surface-2 px-3 py-1 text-xs font-semibold text-pp-text"
                data-testid={`cutest-pluck-tile-my-entry-${entry.id}`}
              >
                You're in the running!
              </span>
            ) : hasVoted ? (
              <Button
                type="button"
                onClick={() => onVote(entry.id)}
                disabled={pending}
                variant="secondary"
                className="rounded-full"
                data-testid={`cutest-pluck-tile-voted-${entry.id}`}
              >
                {pending ? (
                  <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                ) : (
                  <Check className="mr-1 h-4 w-4" />
                )}
                Voted
              </Button>
            ) : (
              <Button
                type="button"
                onClick={() => onVote(entry.id)}
                disabled={pending}
                className="rounded-full bg-pp-purple text-white hover:bg-pp-purple-dark"
                data-testid={`cutest-pluck-tile-vote-${entry.id}`}
              >
                {pending ? (
                  <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                ) : (
                  <Heart className="mr-1 h-4 w-4" />
                )}
                Vote
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CutestPluckLandingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  // Contest prize is CONFIG-DRIVEN — read via ActiveContestContext,
  // which single-fetches `/public/contest/active` on mount. A PATCH to
  // `/admin/contest/config.prize_amount_cents` on prod propagates to
  // the hero copy + OG meta tags without a frontend redeploy (prize-
  // hardcode sweep, 2026-07-23).
  const { prizeAmountCents } = useActiveContest();
  const prizeDisplay = formatPrizeDollars(prizeAmountCents);
  const [sort, setSort] = useState("votes");
  const [entries, setEntries] = useState([]);
  const [myVote, setMyVote] = useState(null); // { entry_id, ... } | null
  const [myEntryId, setMyEntryId] = useState(null);
  const [voteClosesAt, setVoteClosesAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingId, setPendingId] = useState(null);
  const [featuredPhotos, setFeaturedPhotos] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [listResp, featuredResp] = await Promise.allSettled([
        api.get(`/contest/entries?sort=${encodeURIComponent(sort)}`),
        // Phase C — fetch every featured slot in one call so we can
        // both flip the "voting closed" UX (any featured row implies
        // ranking has started) AND render the Hall of Fame section
        // (ranks 4-10). Empty prefix returns all slots.
        api.get("/public/featured-photos"),
      ]);
      if (listResp.status === "fulfilled") {
        setEntries(listResp.value.data?.entries || []);
      }
      if (featuredResp.status === "fulfilled") {
        setFeaturedPhotos(featuredResp.value.data?.featured_photos || []);
      }
      // Best-effort read of the caller's current vote.
      if (user) {
        try {
          const meVote = await api.get("/contest/vote/me");
          setMyVote(meVote.data?.vote || null);
        } catch {
          setMyVote(null);
        }
        // And the caller's own entry (if any) so we can render the
        // "You're in the running!" placeholder over their tile.
        try {
          const meEntry = await api.get("/contest/entries/me");
          setMyEntryId(meEntry.data?.id || null);
        } catch {
          setMyEntryId(null);
        }
      } else {
        setMyVote(null);
        setMyEntryId(null);
      }
    } finally {
      setLoading(false);
    }
  }, [sort, user]);

  useEffect(() => {
    load();
  }, [load]);

  // Feb 2026 Analytics Commit B — `contest.view` fires ONCE on
  // mount of the contest landing surface. Independent of the
  // `load()` effect above so a retry / refetch doesn't double-fire.
  useEffect(() => {
    trackEvent("contest.view");
  }, []);

  async function handleVote(entryId) {
    if (!user) {
      navigate("/login?next=/cutest-pluck", { replace: false });
      return;
    }
    const previousVoteEntryId = myVote?.entry_id || null;
    // Optimistic UI — flip counts + `myVote` before the server round-trip.
    setEntries((cur) =>
      cur.map((e) => {
        if (e.id === entryId) return { ...e, vote_count: e.vote_count + 1 };
        if (previousVoteEntryId && e.id === previousVoteEntryId) {
          return { ...e, vote_count: Math.max(0, e.vote_count - 1) };
        }
        return e;
      }),
    );
    setMyVote({ entry_id: entryId });
    setPendingId(entryId);
    try {
      await api.post(`/contest/entries/${entryId}/vote`);
    } catch (err) {
      // Roll back.
      setEntries((cur) =>
        cur.map((e) => {
          if (e.id === entryId) return { ...e, vote_count: Math.max(0, e.vote_count - 1) };
          if (previousVoteEntryId && e.id === previousVoteEntryId) {
            return { ...e, vote_count: e.vote_count + 1 };
          }
          return e;
        }),
      );
      setMyVote(previousVoteEntryId ? { entry_id: previousVoteEntryId } : null);
      const detail = err?.response?.data?.detail;
      const errCode = typeof detail === "object" ? detail?.error : null;
      if (err?.response?.status === 429 || errCode === "rate_limited") {
        toast.error(
          "Slow down! Your vote is safe — try again in a moment.",
        );
      } else {
        toast.error(
          formatApiErrorDetail(detail) || "Something went wrong. Please try again.",
        );
      }
    } finally {
      setPendingId(null);
    }
  }

  const canonicalUrl = absoluteUrl("/cutest-pluck");
  const heroImageUrl = absoluteUrl("/og/cutest-pluck-hero.jpg");
  // Prize-driven copy read via `formatPrizeDollars(prizeAmountCents)`
  // from ActiveContestContext (see top of module). A prod PATCH to
  // `/admin/contest/config.prize_amount_cents` updates both the
  // Helmet OG description AND the visible hero prize sentence
  // without a frontend redeploy.
  const ogDescription = (
    "Meet the cutest pups in our neighborhoods. Winners get a " +
    `${prizeDisplay} credit toward their first Pluck when booking ` +
    "opens in your area."
  );

  return (
    <PublicPageShell mainClass="px-6 py-12">
      <Helmet>
        <title>{OG_TITLE}</title>
        <meta name="description" content={ogDescription} />
        <meta property="og:title" content={OG_TITLE} />
        <meta property="og:description" content={ogDescription} />
        <meta property="og:image" content={heroImageUrl} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={OG_TITLE} />
        <meta name="twitter:description" content={ogDescription} />
        <meta name="twitter:image" content={heroImageUrl} />
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>

      <div className="mx-auto max-w-6xl">
        {/* Hero */}
        <div className="rounded-3xl bg-pp-surface-2 p-8 sm:p-12">
          <div className="flex flex-col items-start gap-4">
            <div className="inline-flex items-center gap-2 rounded-full bg-pp-surface px-3 py-1 text-xs font-semibold uppercase tracking-wider text-brand-orange">
              <Trophy className="h-3 w-3" aria-hidden="true" />
              Contest
            </div>
            <h1
              className="font-heading text-4xl font-bold text-pp-text sm:text-5xl lg:text-6xl"
              data-testid="cutest-pluck-hero-heading"
            >
              Cutest Pluck on the Block
            </h1>
            <p className="max-w-2xl text-base text-muted-foreground">
              {/* Part A (2026-07-21 dispatch) — pre-launch anticipation
                  framing. We're launching neighborhood by neighborhood;
                  the contest builds local audience while partners sign
                  up. Honest prize framing per human's locked wording:
                  the credit is redeemable when booking opens locally
                  and won't lapse before then. */}
              We're launching PooPluckers neighborhood by neighborhood —
              enter your pup, get the neighborhood excited, and be first
              in line when we go live.
            </p>
            <p
              className="max-w-2xl text-sm text-muted-foreground"
              data-testid="cutest-pluck-hero-prize-honest"
            >
              Winners get a {prizeDisplay} credit toward their first Pluck — yours to
              use the moment booking opens in your area. Credits won't
              expire before service is available.
            </p>
            {voteClosesAt && <CountdownBadge closeAt={voteClosesAt} />}
            <div className="flex flex-wrap items-center gap-3">
              <Link
                to="/contest/enter"
                className="inline-flex h-12 items-center justify-center rounded-full bg-pp-purple px-6 text-base font-semibold text-white hover:bg-pp-purple-dark"
                data-testid="cutest-pluck-enter-cta"
                onClick={() => {
                  // Commit B — capture the click even though the
                  // navigation itself will also fire a page.view.
                  // Two events feed different funnel steps in §4.1.
                  trackEvent("contest.enter_click");
                }}
              >
                Enter your pup
              </Link>
              {/* Rules link — adjacent to the primary CTA. Text
                  underlined so it's clearly a link but styled quieter
                  than the button so it doesn't compete for tap
                  attention. Item A, 2026-07-23. */}
              <Link
                to="/cutest-pluck/rules"
                className="text-sm font-semibold text-pp-text underline decoration-pp-orange decoration-2 underline-offset-4 hover:text-pp-orange"
                data-testid="cutest-pluck-rules-link"
              >
                Contest rules
              </Link>
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="mt-8 flex items-center justify-between">
          <h2 className="font-heading text-2xl font-bold text-pp-text">
            All entries
          </h2>
          <div className="flex items-center gap-2">
            <label
              htmlFor="cutest-pluck-sort"
              className="text-sm font-semibold text-pp-text"
            >
              Sort
            </label>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger
                id="cutest-pluck-sort"
                className="w-32 rounded-full"
                data-testid="cutest-pluck-sort"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="votes" data-testid="cutest-pluck-sort-top">
                  Top
                </SelectItem>
                <SelectItem value="recent" data-testid="cutest-pluck-sort-recent">
                  Recent
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Grid */}
        <div className="mt-6">
          {/* Phase L.2 P2 — audit CP-4. Polite region announces the
              loading→loaded transition of the entry grid. Region
              stays mounted with empty message when idle so SR fires
              on the transition. */}
          <PoliteStatus
            visuallyHidden
            message={
              loading
                ? "Loading entries…"
                : entries.length > 0
                ? `${entries.length} contest ${entries.length === 1 ? "entry" : "entries"} loaded.`
                : ""
            }
            testId="cutest-pluck-loading-status"
          />
          {loading && (
            <div
              className="flex items-center justify-center rounded-2xl border border-border bg-pp-surface p-10"
              data-testid="cutest-pluck-loading"
              aria-busy="true"
            >
              <Loader2 className="h-5 w-5 animate-spin text-brand-orange" aria-hidden="true" />
            </div>
          )}
          {!loading && entries.length === 0 && (
            <div
              className="rounded-2xl border border-border bg-pp-surface p-10 text-center"
              data-testid="cutest-pluck-empty"
            >
              <p className="text-base font-semibold text-pp-text">
                No entries yet — be the first!
              </p>
              <p className="mt-2 text-sm text-muted-foreground">
                Show off your pup and get the neighborhood voting.
              </p>
              <div className="mt-4">
                <Link
                  to="/contest/enter"
                  className="inline-flex h-11 items-center justify-center rounded-full bg-pp-purple px-6 text-sm font-semibold text-white hover:bg-pp-purple-dark"
                  data-testid="cutest-pluck-empty-cta"
                >
                  Enter your pup
                </Link>
              </div>
            </div>
          )}
          {!loading && entries.length > 0 && (
            <div
              className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3"
              data-testid="cutest-pluck-grid"
            >
              {entries.map((e) => (
                <EntryTile
                  key={e.id}
                  entry={e}
                  isMyEntry={myEntryId === e.id}
                  hasVoted={myVote?.entry_id === e.id}
                  pending={pendingId === e.id}
                  onVote={handleVote}
                />
              ))}
            </div>
          )}
        </div>

        {/* Phase C — Voting-closed banner + Hall of Fame section.
            Both surfaces are gated on `featuredPhotos.length > 0`
            (the public featured-photos endpoint returns rows only
            after the admin has assigned at least one rank, which
            by construction requires voting to be closed). */}
        {featuredPhotos.length > 0 && (
          <>
            <div
              className="mt-10 rounded-3xl bg-pp-surface-2 p-6 text-center"
              data-testid="cutest-pluck-voting-closed-banner"
            >
              <p className="font-heading text-xl font-bold text-pp-text">
                Voting is closed for this round
              </p>
              <p className="mt-2 text-sm text-muted-foreground">
                Meet the winners below — see the top 3 on our homepage strip.
              </p>
            </div>
            {(() => {
              const hall = featuredPhotos.filter(
                (p) => p.winner_rank >= 4 && p.winner_rank <= 10,
              );
              if (hall.length === 0) return null;
              return (
                <div
                  className="mt-10"
                  data-testid="cutest-pluck-hall-of-fame"
                >
                  <h2 className="font-heading text-2xl font-bold text-pp-text">
                    Hall of Fame
                  </h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Ranks 4 through 10.
                  </p>
                  <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                    {hall.map((p) => (
                      <Link
                        key={p.slot}
                        to={`/cutest-pluck/entries/${p.entry_id}`}
                        className="overflow-hidden rounded-2xl border border-border bg-pp-surface shadow-sm"
                        data-testid={`cutest-pluck-hall-card-${p.winner_rank}`}
                      >
                        <div className="aspect-square w-full bg-pp-surface-2">
                          {p.photo_url && (
                            <img
                              src={p.photo_url}
                              alt={p.dog_name}
                              className="h-full w-full object-cover"
                              loading="lazy"
                            />
                          )}
                        </div>
                        <div className="p-3">
                          <p className="text-xs font-bold uppercase tracking-wider text-brand-orange">
                            Rank {p.winner_rank}
                          </p>
                          <p className="mt-1 truncate font-heading text-base font-bold text-pp-text">
                            {p.dog_name}
                          </p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              );
            })()}
          </>
        )}
        <WinnerSlotCard winnerRank={4} />
      </div>
    </PublicPageShell>
  );
}
